<template>
  <v-app>
    <!-- Layout component -->
    <component :is="currentLayout" v-if="isRouterLoaded">
      <transition name="fade" mode="out-in">
        <router-view />
      </transition>
    </component>

    <v-snackbar
      v-model="toast.show"
      :timeout="toast.timeout"
      :color="toast.color"
      bottom
    >
      {{ toast.message }}
      <v-btn
        v-if="toast.timeout === 0"
        color="white"
        text
        @click="toast.show = false"
        >{{ $t('common.close') }}</v-btn
      >
    </v-snackbar>
  </v-app>
</template>

<script>
  import { mapState } from 'vuex';
  import config from './configs';

  // Layouts
  import onePageLayout from './layouts/OnePageLayout';

  /*
  |---------------------------------------------------------------------
  | Main Application Component
  |---------------------------------------------------------------------
  |
  | In charge of choosing the layout according to the router metadata
  |
  */
  export default {
    components: {
      onePageLayout
    },
    mounted() {
      this.setViewportForMobile();
    },
    methods: {
      setViewportForMobile() {
        if (this.$vuetify.breakpoint.xs) {
          // Check if viewport meta tag already exists
          let viewportMeta = document.querySelector('meta[name="viewport"]');
          if (!viewportMeta) {
            viewportMeta = document.createElement('meta');
            viewportMeta.name = 'viewport';
            document.head.appendChild(viewportMeta);
          }
          viewportMeta.content = 'width=980, initial-scale=0.5';
        }
      }
    },
    computed: {
      ...mapState('app', ['toast']),
      isRouterLoaded: function () {
        if (this.$route.name !== null) return true;

        return false;
      },
      currentLayout: function () {
        const layout = this.$route.meta.layout || 'onePage';

        return layout + 'Layout';
      }
    },
    head: {
      link: [
        // adds config/icons into the html head tag
        ///...config.icons.map((href) => ({ rel: 'stylesheet', href }))
      ]
    }
  };
</script>

<style>
  @media only screen and (max-width: 600px) {
    /* Mobile-specific styles */
    body {
      width: 980px;
      margin: 0 auto;
    }
  }
  router-link {
    text-decoration: none; /* Remove underline */
  }
  @font-face {
    font-family: 'Neue Plak';
    src: url('~@/assets/fonts/NeuePlak-Regular.ttf') format('truetype');
  }
  @font-face {
    font-family: 'Inter';
    src: url('~@/assets/fonts/Inter-Regular.ttf') format('truetype');
  }
  @font-face {
    font-family: 'Inter V';
    src: url('~@/assets/fonts/Inter-V.ttf') format('truetype');
  }
  .button16 {
    font-size: 16px;
  }
  .sigflo-h1 {
    /* H1 */
    font-family: 'Inter V';
    font-size: 48px;
    font-style: normal;
    font-weight: 600;
    line-height: 49px; /* 102.083% */
    letter-spacing: 1.168px;
  }
  .sigflo-h3 {
    font-family: 'Inter V';
    font-size: 36px;
    font-style: normal;
    font-weight: 800;
    line-height: 38px; /* 105.556% */
    letter-spacing: 1.168px;
  }
  .sigflo-h4 {
    /* H4 */
    font-family: 'Inter V';
    font-size: 32px;
    font-style: normal;
    font-weight: 400;
    line-height: 42px; /* 131.25% */
    letter-spacing: 1.168px;
  }
  .sigflo-h5 {
    /* H5 */
    font-family: 'Neue Plak';
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: 26px; /* 108.333% */
    letter-spacing: 1.168px;
  }
  .sigflo-buttonText {
    font-family: 'Inter V';
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: capitalize;
  }
  .sigflo-s {
    /* Small */
    font-family: 'Inter V';
    font-size: 48px;
    font-style: normal;
    font-weight: 700;
    line-height: 54px; /* 112.5% */
    letter-spacing: -1px;
  }
  .sigflo-m {
    /* Medium */
    font-family: 'Neue Plak';
    font-size: 56px;
    font-style: normal;
    font-weight: 400;
    line-height: 54px; /* 96.429% */
  }
  .sigflo-l {
    /* Large */
    font-family: 'Neue Plak';
    font-size: 64px;
    font-style: normal;
    font-weight: 600;
    line-height: 62px; /* 96.875% */
  }
  .sigflo-xl {
    /* Xl */
    font-family: 'Neue Plak';
    font-size: 84px;
    font-style: normal;
    font-weight: 500;
    line-height: 85px; /* 101.19% */
  }
  .sigflo-2xl {
    /* 2xl */
    font-family: 'Neue Plak';
    font-size: 96px;
    font-style: normal;
    font-weight: 500;
    line-height: 94px; /* 97.917% */
  }
  .sigflo-body {
    /* Body */
    font-family: Inter;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 26px; /* 144.444% */
  }
  .sigflo-bodySmall {
    /* Body Small */
    font-family: 'Inter V';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px; /* 128.571% */
  }
  .sigflo-bodyLarge {
    /* Body Large */
    font-family: 'Inter V';
    font-size: 22px;
    font-style: normal;
    font-weight: 400;
    line-height: 31px; /* 140.909% */
    letter-spacing: 0.458px;
  }
  .sigflo-bodyMedium {
    /* Body Large */
    font-family: 'Inter V';
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 31px; /* 140.909% */
    letter-spacing: 0.458px;
  }
  .mutedWhiteText {
    color: var(--Muted, #f6f0f9);
  }
  .sigflo-bodyLead {
    font-family: 'Inter V';
    font-size: 24px !important;
    font-style: normal;
    font-weight: 600;
    line-height: 31px; /* 129.167% */
    letter-spacing: 0.5px;
  }
  .greenText {
    color: var(--Success, var(--White, #32ff99));
  }
  .pinkText {
    color: var(--Danger, var(--White, #e937c7));
  }
  .blueText {
    color: #0121f7;
  }
  .purpleText {
    color: var(--Secondary, #7c40dc);
  }
  .userSystemColumn {
    max-width: 430px;
  }
  .venueColumn {
    max-width: 430px;
  }
  /* Vuetify Overrides */
  .theme--dark.v-btn.v-btn--icon {
    color: #7c40dc !important;
  }
  .v-carousel__controls__item.v-btn.v-btn--icon {
    background-color: rgba(
      124,
      64,
      220,
      0.1
    ); /* Background color of non-active ones */
    height: 15px; /* Height you want */
    width: 15px; /* Width you want */
    border-radius: 15px; /* Remove default border radius */
  }

  .v-carousel__controls__item.v-btn.v-btn--icon.v-btn--active {
    background-color: #7c40dc; /* Colour for active one */
  }

  .v-carousel__controls__item.v-btn.v-btn--icon:hover {
    background-color: black; /* You might also want to customise the hover effect */
  }

  .v-btn__content .v-icon {
  }
</style>
